/**
 * UserProfile Manager - localStorage-based user identity
 * Provides persistent user profiles without backend authentication
 */

export type GameType =
  | 'guesswho'
  | 'tictactoe'
  | 'gofish'
  | 'bigtwo'
  | 'werewolf'
  | 'pictionary'
  | 'themind'
  | 'codenames'
  | 'wavelength'

export interface GameStats {
  played: number
  wins: number
  losses: number
  lastPlayed?: string // ISO timestamp
}

export interface UserProfile {
  id: string // unique identifier
  name: string
  avatar?: string // emoji or image URL
  gamesPlayed: number
  wins: number
  createdAt: string // ISO timestamp
  lastActive: string // ISO timestamp
  gameStats?: Record<GameType, GameStats> // Per-game statistics
}

const STORAGE_KEY = 'zgames_user_profile'

export const UserProfileManager = {
  /**
   * Get the current user profile from localStorage
   */
  get: (): UserProfile | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return null

      const profile = JSON.parse(stored) as UserProfile

      // Update last active timestamp
      profile.lastActive = new Date().toISOString()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile))

      return profile
    } catch (error) {
      console.error('Failed to get user profile:', error)
      return null
    }
  },

  /**
   * Save a new user profile
   */
  save: (name: string, avatar?: string): UserProfile => {
    const profile: UserProfile = {
      id: generateUserId(),
      name: name.trim(),
      avatar: avatar || generateRandomAvatar(),
      gamesPlayed: 0,
      wins: 0,
      createdAt: new Date().toISOString(),
      lastActive: new Date().toISOString(),
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile))
    return profile
  },

  /**
   * Update existing profile
   */
  update: (updates: Partial<UserProfile>): UserProfile | null => {
    const current = UserProfileManager.get()
    if (!current) return null

    const updated = {
      ...current,
      ...updates,
      lastActive: new Date().toISOString(),
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    return updated
  },

  /**
   * Increment games played counter
   */
  incrementGamesPlayed: (): void => {
    const profile = UserProfileManager.get()
    if (profile) {
      UserProfileManager.update({ gamesPlayed: profile.gamesPlayed + 1 })
    }
  },

  /**
   * Increment wins counter
   */
  incrementWins: (): void => {
    const profile = UserProfileManager.get()
    if (profile) {
      UserProfileManager.update({ wins: profile.wins + 1 })
    }
  },

  /**
   * Delete user profile
   */
  delete: (): void => {
    localStorage.removeItem(STORAGE_KEY)
  },

  /**
   * Check if user has a profile
   */
  exists: (): boolean => {
    return UserProfileManager.get() !== null
  },

  /**
   * Track game completion
   */
  trackGamePlayed: (didWin: boolean = false): void => {
    const profile = UserProfileManager.get()
    if (profile) {
      UserProfileManager.update({
        gamesPlayed: profile.gamesPlayed + 1,
        wins: didWin ? profile.wins + 1 : profile.wins,
      })
    }
  },

  /**
   * Track game completion with game type
   */
  trackGameByType: (gameType: GameType, didWin: boolean = false): void => {
    const profile = UserProfileManager.get()
    if (!profile) return

    // Initialize gameStats if it doesn't exist
    const gameStats = profile.gameStats || {} as Record<GameType, GameStats>

    // Initialize stats for this game if it doesn't exist
    if (!gameStats[gameType]) {
      gameStats[gameType] = { played: 0, wins: 0, losses: 0 }
    }

    // Update stats for this specific game
    gameStats[gameType].played += 1
    if (didWin) {
      gameStats[gameType].wins += 1
    } else {
      gameStats[gameType].losses += 1
    }
    gameStats[gameType].lastPlayed = new Date().toISOString()

    // Update overall stats + game-specific stats
    UserProfileManager.update({
      gamesPlayed: profile.gamesPlayed + 1,
      wins: didWin ? profile.wins + 1 : profile.wins,
      gameStats,
    })
  },

  /**
   * Get stats for a specific game
   */
  getGameStats: (gameType: GameType): GameStats => {
    const profile = UserProfileManager.get()
    if (!profile || !profile.gameStats) {
      return { played: 0, wins: 0, losses: 0 }
    }
    return profile.gameStats[gameType] || { played: 0, wins: 0, losses: 0 }
  },

  /**
   * Get favorite game (most played)
   */
  getFavoriteGame: (): { game: GameType; stats: GameStats } | null => {
    const profile = UserProfileManager.get()
    if (!profile || !profile.gameStats) return null

    let maxPlayed = 0
    let favoriteGame: GameType | null = null

    Object.entries(profile.gameStats).forEach(([game, stats]) => {
      if (stats.played > maxPlayed) {
        maxPlayed = stats.played
        favoriteGame = game as GameType
      }
    })

    if (!favoriteGame) return null

    return {
      game: favoriteGame,
      stats: profile.gameStats[favoriteGame],
    }
  },
}

/**
 * Generate a unique user ID
 */
function generateUserId(): string {
  return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Generate a random avatar emoji
 */
function generateRandomAvatar(): string {
  const avatars = [
    '😀', '😎', '🤓', '🥳', '🤩', '😈', '👽', '🤖', '🎮', '🎯',
    '🎲', '🎪', '🎨', '🎭', '🎸', '🚀', '⚡', '🔥', '✨', '🌟',
    '🦄', '🐉', '🦊', '🐺', '🦁', '🐯', '🐸', '🐙', '🦋', '🌈',
  ]
  return avatars[Math.floor(Math.random() * avatars.length)]
}
